import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { TypeMark } from './DeliverableGroups';
import type { FileRow } from '../types/portal';

/**
 * The Files list on the client side.
 *
 * 26 Sep 2026 — THESE BUTTONS USED TO DO NOTHING.
 *
 * Every row called `notify('Simulated · <name> would download from Google
 * Drive')`, with no prototype flag and no environment check. So on the LIVE
 * client site, a client of a video production company pressed Download and
 * was told their delivery was simulated. It had been that way for weeks.
 *
 * Two lessons worth keeping:
 *
 *   • A placeholder that LOOKS finished is worse than an obviously missing
 *     feature. Nobody noticed, because the button was styled, positioned and
 *     responsive. It simply lied.
 *   • `PROTOTYPE_MODE` guards the staff/client view switcher properly, which
 *     made it easy to assume everything else was guarded too. **If a control
 *     is a stub, put it behind that flag the same day.**
 *
 * WHY THESE OPEN DRIVE RATHER THAN DOWNLOADING DIRECTLY. Drive's direct
 * download endpoint (`uc?export=download&id=`) streams small files fine, but
 * anything over roughly 100MB meets a "Google Drive can't scan this file for
 * viruses" interstitial. Portal's finals run 280MB to 1.4GB, so that warning
 * is what a client would see almost every time — which reads far worse than
 * Drive's own file view, where Download is the first control on screen.
 *
 * This is the honest floor, not the destination. A real in-page download
 * means serving the media from somewhere built for it; until then, opening
 * the file where it actually lives is the version that always works.
 *
 * A ROW WITH NO LINK IS NOT CLICKABLE. If `driveUrl` is empty there is
 * nothing to open, so the row renders as plain text rather than as a control
 * that does nothing — which is the whole mistake above, in miniature.
 */

interface FileListProps {
  rows: FileRow[];
}
export function FileList({
  rows
}: FileListProps) {
  const {
    openDeliverable
  } = usePortal();
  return <ul className="m-0 list-none p-0" data-id="element-400" data-mp="1hgstwoa2yh2d">
      {rows.map(row => {
      /* Same innards whichever element wraps them, so a transcript row, a
         Drive row and an unlinked row are visibly one family. */
      const body = <>
            <TypeMark type={row.type} driveUrl={row.driveUrl} data-id="element-401" data-mp="1qemljlwwgmj7" />
            <span className="min-w-0 flex-1" data-id="element-402" data-mp="12rwn0ujrfa3c">
              <span className="block font-display text-2xl leading-none sm:text-3xl" data-id="element-403" data-mp="xs1wwt7t6ojc">
                {row.label}
              </span>
              <span className="label-sm mt-3 block text-mute" data-id="element-404" data-mp="1ch7rg3vpbqkw">{row.meta}</span>
            </span>
          </>;
      const shell = 'group flex w-full items-center gap-5 py-6 text-left transition-colors duration-150 ease-out';
      if (row.opensInPortal) {
        return <li key={row.id} className="border-b border-rule" data-id="element-405" data-mp="f2c0k1oedupb">
              <button type="button" onClick={() => openDeliverable(row.id)} className={`${shell} hover:text-graphite`} data-id="element-406" data-mp="2qow2u16uy957">
                {body}
                <span className="label flex shrink-0 items-center gap-2 text-graphite" data-id="element-407" data-mp="2hohfkwac2jz2">
                  {row.action}
                  <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" strokeWidth={1.25} data-id="element-408" data-mp="2lp1d40ytjplq" />
                </span>
              </button>
            </li>;
      }
      if (!row.driveUrl) {
        return <li key={row.id} className="border-b border-rule" data-id="element-409" data-mp="30efofelygh3r">
              <div className={shell} data-id="element-410" data-mp="35d0lj55wuknz">{body}</div>
            </li>;
      }
      return <li key={row.id} className="border-b border-rule" data-id="element-411" data-mp="1oyilgmha7wnj">
            <a href={row.driveUrl} target="_blank" rel="noopener noreferrer" className={`${shell} no-underline hover:text-graphite`} data-id="element-412" data-mp="1tyn52ldhl7hz">
              {body}
              <span className="label flex shrink-0 items-center gap-2 text-graphite" data-id="element-413" data-mp="24hbhiyjbi9vz">
                {row.action}
                <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" strokeWidth={1.25} data-id="element-414" data-mp="1zjia626a39u4" />
              </span>
            </a>
          </li>;
    })}
    </ul>;
}